import { memo, useCallback, useContext, useState } from "react";
import fraction from "src/api/fraction";
import { ApplicationFlowStateContext } from "src/apps/AppFlow/screens/root";
import { useIdentityVerificationQuestionsWithContext } from "src/apps/AppFlow/screens/root/Layout/sections/IdentityVerification/query";
import { useEffectOnce } from "src/hooks";

import { ValueError, enums, sleep } from "@fraction/shared";

import IdVerificationPreambleLayout, { IdVerificationPreambleLayoutProps } from "./Layout";

export interface IdVerificationPreambleProps
  extends Omit<IdVerificationPreambleLayoutProps, "onChange" | "onNext"> {
  onNext: () => void;
}

const IdVerificationPreamble = ({ onNext, ...props }: IdVerificationPreambleProps) => {
  const { state, dispatch } = useContext(ApplicationFlowStateContext);
  const [manualLoading, setManualLoading] = useState(false);

  // this page is before the idVerification pages
  // ideally, we would be able to choose "page before X" rather than having to manually select
  // the page before what we want, but that would require a bigger refactor
  const { initiateVerification, loading, decision } = useIdentityVerificationQuestionsWithContext({
    state,
    dispatch,
  });

  useEffectOnce(
    () => {
      // we might be tempted to just run the initiateVerification on an onNext call, but for some buggy
      // reason that I don't want to figure out right now, that wasn't causing the onNext to properly fire,
      // so just running it as soon as this page loads. This isn't too bad, as it means the page is more
      // responsive anyways
      const primaryApplicantId = state.applicants?.[0]?.id;
      if (!primaryApplicantId) {
        throw new ValueError("Primary applicant does not have ID at ID verification stage");
      }
      initiateVerification({ applicantId: primaryApplicantId });
    },
    [initiateVerification, onNext, state.applicants],
    !state.applicants?.[0]?.id || !fraction.authenticated
  );

  const handleNext = useCallback(async () => {
    // if it already failed before we began, let's pretend there's some computation going on so that it's not jarring for users
    if (
      !fraction.authenticated ||
      !state.applicants?.[0]?.id ||
      decision === enums.IdVerificationDecision.EXPLICIT_FAIL ||
      decision === enums.IdVerificationDecision.IMPLICIT_FAIL
    ) {
      setManualLoading(true);
      await sleep(2000);
      setManualLoading(false);
    }
    onNext?.();
  }, [decision, onNext, state.applicants]);

  return (
    <IdVerificationPreambleLayout {...props} nextLoading={loading || manualLoading} onNext={handleNext} />
  );
};

export default memo(IdVerificationPreamble);
